/**
 * EmptyStates — the designed empty state for each Command Center pillar tile.
 *
 * Empty means the pillar's primary metric is unavailable (nothing assessed
 * yet, or no framework assigned). It is NOT "100% healthy": a tenant with
 * zero drifts still has a score, so the regular tile handles that.
 *
 * Carbon order, headline then body (the No-Kicker Rule), on the shared
 * EmptyState. Inside a pillar tile the whole tile is already a link, so the
 * tile passes no `ctaHref` and no nested link is drawn; a standalone caller
 * can still pass one for a tertiary button.
 */

import EmptyState from '@/components/shared/EmptyState'

type EmptyKind = 'compliance' | 'bpa' | 'identity'

interface Props {
  kind: EmptyKind
  /** Optional CTA route, drawn as a tertiary button. Omit inside a link tile. */
  ctaHref?: string
}

const COPY: Record<EmptyKind, { headline: string; body: string; ctaLabel: string }> = {
  compliance: {
    headline: 'Your baseline has no assessment yet.',
    body: 'The score and the open drifts by severity appear after the first assessment.',
    ctaLabel: 'Open Baseline Compliance',
  },
  bpa: {
    headline: 'Assign a framework to score Best Practice.',
    body: 'MCSB, EIDSCA, NIST, SCuBA and CIS are available. Guardian checks your tenant against every control in the frameworks you pick.',
    ctaLabel: 'Open Security Center',
  },
  identity: {
    headline: 'Identity data arrives with the first user refresh.',
    body: 'Guardian then shows MFA coverage for every enabled member, and User Analytics names the members who still need MFA.',
    ctaLabel: 'Open User Analytics',
  },
}

export default function PillarEmptyState({ kind, ctaHref }: Props) {
  const { headline, body, ctaLabel } = COPY[kind]
  return <EmptyState headline={headline} body={body} cta={ctaHref ? { label: ctaLabel, to: ctaHref } : undefined} />
}

/**
 * Convenience wrappers: name the empty state at the call site for readability.
 */
export function ComplianceEmpty({ ctaHref }: { ctaHref?: string }) {
  return <PillarEmptyState kind="compliance" ctaHref={ctaHref} />
}
export function BpaEmpty({ ctaHref }: { ctaHref?: string }) {
  return <PillarEmptyState kind="bpa" ctaHref={ctaHref} />
}
export function IdentityEmpty({ ctaHref }: { ctaHref?: string }) {
  return <PillarEmptyState kind="identity" ctaHref={ctaHref} />
}
